import React from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Modal from "@mui/material/Modal";
import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RefreshIcon from "@mui/icons-material/Refresh";
import StorefrontIcon from "@mui/icons-material/Storefront";

const PROVIDER_LABELS = {
  toast_connected: "Toast",
  heartland_retail_connected: "Heartland Retail",
  square_connected: "Square",
};

function DetailCard({ label, value, tone = "slate" }) {
  const toneClasses =
    tone === "amber"
      ? "border-amber-200 bg-amber-50 text-amber-900"
      : "border-slate-200 bg-white text-slate-900";

  return (
    <div className={`rounded-lg border px-4 py-3 ${toneClasses}`}>
      <p
        className={`text-[11px] font-semibold uppercase tracking-[0.14em] ${
          tone === "amber" ? "text-amber-700" : "text-slate-500"
        }`}
      >
        {label}
      </p>
      <p className="mt-1.5 text-xl font-semibold">{value}</p>
    </div>
  );
}

function ConnectedPOSDetailsModal(props) {
  const {
    open,
    onClose,
    company,
    posStores,
    posLinkedCount,
    onRefreshLocations,
    onOpenImportStores,
    isRefreshing,
  } = props;

  const providerLabel =
    PROVIDER_LABELS[company?.pos_integration] || "POS";
  const unimportedCount = posStores?.length || 0;
  const linkedCount = posLinkedCount || 0;

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        className="absolute top-1/2 left-1/2 w-[92vw] max-w-2xl -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white shadow-xl outline-none"
      >
        <div className="relative max-h-[90vh] overflow-y-auto rounded-lg">
          <div className="border-b border-slate-200 px-6 pb-5 pt-6">
            <IconButton
              onClick={onClose}
              aria-label="Close"
              sx={{
                position: "absolute",
                top: 14,
                right: 14,
                color: "rgb(71 85 105)",
              }}
            >
              <CloseIcon />
            </IconButton>

            <div className="max-w-[34rem]">
              <div className="inline-flex items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                <CheckCircleIcon sx={{ fontSize: 16 }} />
                Connected to {providerLabel}
              </div>
              <h2 className="mt-4 text-xl font-semibold text-slate-900">
                Integration details
              </h2>
              <p className="mt-2 text-sm leading-6 text-slate-500">
                Refresh your company’s POS locations using the saved credentials, then import any newly discovered locations into Rightwork.
              </p>
            </div>
          </div>

          <div className="bg-slate-50 px-6 py-6">
            <div className="grid gap-4 sm:grid-cols-3">
              <DetailCard
                label="Company"
                value={company?.company_name || company?.name || "Current company"}
              />
              <DetailCard label="Imported" value={linkedCount} />
              <DetailCard label="Needs review" value={unimportedCount} tone="amber" />
            </div>

            <div className="mt-5 rounded-lg border border-slate-200 bg-white px-4 py-4">
              <div className="flex gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-purple-50 text-[#6d28d9]">
                  <StorefrontIcon sx={{ fontSize: 20 }} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900">
                    Refresh company locations
                  </p>
                  <p className="mt-1 text-sm leading-6 text-slate-500">
                    Use this after updating credentials manually or when new locations were added in {providerLabel}. Rightwork will pull the latest company locations again.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              {unimportedCount > 0 && (
                <button
                  type="button"
                  onClick={onOpenImportStores}
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-amber-300 bg-white px-4 py-2.5 text-sm font-medium text-amber-800 transition hover:bg-amber-50"
                >
                  <StorefrontIcon sx={{ fontSize: 18 }} />
                  Review locations
                </button>
              )}
              <button
                type="button"
                onClick={onRefreshLocations}
                disabled={isRefreshing}
                className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium text-white transition ${
                  isRefreshing
                    ? "cursor-not-allowed bg-purple-300"
                    : "bg-[#6d28d9] hover:bg-[#5b21b6]"
                }`}
              >
                {isRefreshing ? (
                  <>
                    <CircularProgress size={16} sx={{ color: "white" }} />
                    Refreshing locations
                  </>
                ) : (
                  <>
                    <RefreshIcon sx={{ fontSize: 18 }} />
                    Refetch company info
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </Box>
    </Modal>
  );
}

export default ConnectedPOSDetailsModal;
